import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import {
  extractExtramileReportId,
  getSplitCanvasPair,
  saveSplitCanvasSideReportId,
  uploadSplitCanvasDrawing,
} from "../../functions/splitCanvasApi";
import { sendReport } from "../../functions/sendReport";
import { SPLIT_CANVAS_POST_POINTS } from "../../functions/splitCanvasReportPoints";
import part2Img from "../../img/assets/part-2.png";
import brushIcon from "../../img/assets/brush.png";
import eraserIcon from "../../img/assets/eraser.png";
import dropperIcon from "../../img/assets/dropper.png";
import fillIcon from "../../img/assets/fill.png";
import ColorWheel from "./ColorWheel";
import { floodFillComposite, rgbDistanceSq } from "./floodFill";
import { hsvToHex, hsvToRgb, rgbToHsv } from "./colorUtils";
import "./drawPage.css";

const SPLIT_CTX_KEY = "split_canvas_ctx_v1";
const DRAW_TIMER_STORAGE_PREFIX = "split_canvas_draw_timer_v1";

function drawTimerStorageKey(sessionId, userId, pairId, side) {
  const sid =
    sessionId != null && String(sessionId).trim() !== ""
      ? String(sessionId)
      : "no-session";
  const uid =
    userId != null && String(userId).trim() !== ""
      ? String(userId)
      : "no-user";
  return `${DRAW_TIMER_STORAGE_PREFIX}__${sid}__${uid}__${String(pairId)}__${String(side)}`;
}

function readDrawTimerDeadline(storageKey) {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.deadlineMs === "number") return parsed.deadlineMs;
  } catch {
    /* ignore */
  }
  return null;
}

function writeDrawTimerDeadline(storageKey, deadlineMs) {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify({ deadlineMs }));
  } catch {
    /* ignore */
  }
}

function clearDrawTimer(storageKey) {
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    /* ignore */
  }
}

function s3PublicUrl(filename) {
  if (!filename) return "";
  const base = process.env.REACT_APP_S3_PATH || "";
  const key = String(filename)
    .split("/")
    .map((part) => encodeURIComponent(part))
    .join("/");
  return base.endsWith("/") ? `${base}${key}` : `${base}/${key}`;
}

function parseSplitSets(themeData) {
  let sets = themeData?.splitImageSets;
  if (sets == null) return [];
  if (typeof sets === "string") {
    try {
      sets = JSON.parse(sets);
    } catch {
      return [];
    }
  }
  return Array.isArray(sets) ? sets : [];
}
const MAX_HISTORY = 50;
const BRUSH_THICKNESS_MAX = 24;
const TARGET_CANVAS = 450;
const MIN_CANVAS = 48;

function formatTime(totalSec) {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function strokeWidthForTool(tool, brushThickness) {
  if (tool === "eraser" || tool === "brush") return brushThickness;
  if (tool === "fill") return brushThickness;
  return brushThickness;
}

/** Skip fill when brush color is already very close to seed (RGB distance). */
const FILL_SKIP_MAX_DIST = 22;
const FILL_SKIP_DIST_SQ = FILL_SKIP_MAX_DIST * FILL_SKIP_MAX_DIST;
/** Fixed flood-fill color distance (no UI slider). */
const FILL_COLOR_TOLERANCE = 52;

export default function DrawPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);
  const themeData = useSelector((s) => s.theme.data);

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const submitOnceRef = useRef(false);
  const [baseSize, setBaseSize] = useState(null);

  const [splitCtx, setSplitCtx] = useState(null);
  const [pairBlocked, setPairBlocked] = useState(false);
  const [ctxError, setCtxError] = useState(null);
  const [baseImageUrl, setBaseImageUrl] = useState(part2Img);
  const [submitting, setSubmitting] = useState(false);
  const [timerInitialized, setTimerInitialized] = useState(false);
  const [baseImageRetryCount, setBaseImageRetryCount] = useState(0);
  const [baseImageLoadError, setBaseImageLoadError] = useState(false);
  const [infoModal, setInfoModal] = useState(null);

  const timerEnabled = useMemo(() => {
    const t = parseInt(themeData?.wmlQuestionTimerSeconds, 10);
    return Number.isFinite(t) && t > 0;
  }, [themeData]);

  const [secondsLeft, setSecondsLeft] = useState(0);
  const [tool, setTool] = useState("brush");
  const [hsv, setHsvState] = useState({ h: 0, s: 1, v: 1 });
  const { h, s, v } = hsv;
  const [brushThickness, setBrushThickness] = useState(8);
  const [ready, setReady] = useState(false);
  const [cursorUi, setCursorUi] = useState({ visible: false, x: 0, y: 0 });
  const [supportsFinePointer, setSupportsFinePointer] = useState(false);

  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });

  const color = useMemo(() => hsvToHex(h, s, v), [h, s, v]);
  const cursorToolIconSrc = useMemo(() => {
    if (tool === "fill") return fillIcon;
    if (tool === "eyedropper") return dropperIcon;
    return fillIcon;
  }, [tool]);

  const setHsv = useCallback((next) => {
    setHsvState((prev) => ({ ...prev, ...next }));
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/get-pairing"));
  }, [dispatch]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia("(pointer: fine)");
    const sync = () => setSupportsFinePointer(Boolean(mql.matches));
    sync();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", sync);
      return () => mql.removeEventListener("change", sync);
    }
    if (typeof mql.addListener === "function") {
      mql.addListener(sync);
      return () => mql.removeListener(sync);
    }
  }, []);

  useEffect(() => {
    if (!themeData) return;
    const t = parseInt(themeData.wmlQuestionTimerSeconds, 10);
    const durationSec = Number.isNaN(t) ? 0 : Math.max(0, t);
    if (durationSec <= 0) {
      setSecondsLeft(0);
      setTimerInitialized(true);
      return;
    }
    if (!splitCtx?.pairId || !user?.userId) return;

    const storageKey = drawTimerStorageKey(
      user.sessionId,
      user.userId,
      splitCtx.pairId,
      splitCtx.side
    );
    const now = Date.now();
    const existingDeadline = readDrawTimerDeadline(storageKey);
    if (existingDeadline != null && existingDeadline > now) {
      const remaining = Math.max(
        0,
        Math.ceil((existingDeadline - now) / 1000)
      );
      setSecondsLeft(remaining);
    } else {
      const deadlineMs = now + durationSec * 1000;
      setSecondsLeft(durationSec);
      writeDrawTimerDeadline(storageKey, deadlineMs);
    }
    setTimerInitialized(true);
  }, [themeData, splitCtx, user]);

  useEffect(() => {
    let raw = location.state;
    if (!raw) {
      try {
        const s = sessionStorage.getItem(SPLIT_CTX_KEY);
        if (s) raw = JSON.parse(s);
      } catch {
        raw = null;
      }
    }
    if (!raw?.pairId || !raw?.side || raw.setIndex == null || !raw?.themeName) {
      setCtxError("missing_context");
      return;
    }
    setSplitCtx({
      pairId: raw.pairId,
      side: raw.side,
      setIndex: Number(raw.setIndex),
      themeName: String(raw.themeName),
    });
  }, [location.state]);

  useEffect(() => {
    if (!splitCtx?.themeName || !token) return;
    const loadedThemeName =
      themeData?.themename || themeData?.themeName || "";
    if (loadedThemeName === splitCtx.themeName) return;
    dispatch(fetchThemeData({ themeId: splitCtx.themeName }));
  }, [dispatch, splitCtx, token, themeData]);

  useEffect(() => {
    if (!splitCtx || !token || !user?.userId) return;
    let cancelled = false;
    (async () => {
      try {
        const { pair } = await getSplitCanvasPair(token, splitCtx.pairId);
        if (cancelled || !pair) return;
        const uid = String(user.userId);
        const mine =
          splitCtx.side === "left"
            ? pair.left_submitted_at
            : pair.right_submitted_at;
        if (mine) {
          setPairBlocked(true);
          navigate("/gallery", { replace: true });
        }
      } catch (e) {
        console.warn("[draw] pair check", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [splitCtx, token, user, navigate]);

  useEffect(() => {
    if (!splitCtx || !themeData) return;
    const sets = parseSplitSets(themeData);
    const row = sets[splitCtx.setIndex];
    if (!row) {
      setCtxError("no_image_set");
      return;
    }
    const key = splitCtx.side === "left" ? row.left : row.right;
    if (!key) {
      setCtxError("no_side_image");
      return;
    }
    setBaseImageRetryCount(0);
    setBaseImageLoadError(false);
    setBaseImageUrl(s3PublicUrl(key));
  }, [splitCtx, themeData]);

  useEffect(() => {
    if (!timerEnabled) return undefined;
    if (secondsLeft <= 0) return undefined;
    const id = window.setTimeout(() => {
      setSecondsLeft((t) => (t <= 1 ? 0 : t - 1));
    }, 1000);
    return () => clearTimeout(id);
  }, [timerEnabled, secondsLeft]);

  useEffect(() => {
    if (
      !timerEnabled ||
      !timerInitialized ||
      !splitCtx?.pairId ||
      !user?.userId
    )
      return;
    const storageKey = drawTimerStorageKey(
      user.sessionId,
      user.userId,
      splitCtx.pairId,
      splitCtx.side
    );
    if (secondsLeft <= 0) {
      clearDrawTimer(storageKey);
      return;
    }
    writeDrawTimerDeadline(storageKey, Date.now() + secondsLeft * 1000);
  }, [timerEnabled, timerInitialized, secondsLeft, splitCtx, user]);

  const buildCompositeBlob = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img?.complete) return Promise.reject(new Error("not ready"));
    return new Promise((resolve, reject) => {
      const w = canvas.width;
      const h = canvas.height;
      const out = document.createElement("canvas");
      out.width = w;
      out.height = h;
      const ctx = out.getContext("2d");
      if (!ctx) {
        reject(new Error("no ctx"));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      ctx.drawImage(canvas, 0, 0);
      try {
        out.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error("blob"));
          },
          "image/png",
          0.92
        );
      } catch (e) {
        reject(
          new Error(
            "Image export blocked by S3 CORS. Please allow GET origin for this app."
          )
        );
      }
    });
  }, []);

  const handleSubmitDrawing = useCallback(async () => {
    if (!splitCtx || !token || !user?.userId || submitting || pairBlocked)
      return;
    if (baseImageLoadError) {
      setInfoModal({
        title: "Image not loaded",
        message:
          "Base image failed to load from S3. Please retry after image loads.",
        actionLabel: "OK",
      });
      return;
    }
    if (submitOnceRef.current) return;
    submitOnceRef.current = true;
    setSubmitting(true);
    try {
      const blob = await buildCompositeBlob();
      const fd = new FormData();
      fd.append("file", blob, `draw-${splitCtx.pairId}-${splitCtx.side}.png`);
      fd.append("pairId", String(splitCtx.pairId));
      fd.append("side", splitCtx.side);
      fd.append("userId", String(user.userId));
      await uploadSplitCanvasDrawing(token, fd);

      if (user.sessionId) {
        const sideReportId =
          splitCtx.side === "left"
            ? splitCtx.leftReportId
            : splitCtx.rightReportId;
        try {
          const reportRes = await sendReport({
            sessionId: user.sessionId,
            role: user.role,
            token: user.token || null,
            name: user.name,
            userId: user.userId,
            gameId: user.gameId || "",
            organizationId: user.organizationId,
            points: SPLIT_CANVAS_POST_POINTS,
            time: "00:00:00",
            reportId: sideReportId || user.reportId || null,
            ans: "",
          });
          const newId = extractExtramileReportId(reportRes);
          if (newId && splitCtx?.pairId) {
            await saveSplitCanvasSideReportId(
              token,
              splitCtx.pairId,
              splitCtx.side,
              newId
            );
          }
        } catch (reportErr) {
          console.error("[draw] sendReport / saveReportId", reportErr);
        }
      }

      clearDrawTimer(
        drawTimerStorageKey(
          user.sessionId,
          user.userId,
          splitCtx.pairId,
          splitCtx.side
        )
      );
      navigate("/gallery", { replace: true });
    } catch (e) {
      console.error(e);
      submitOnceRef.current = false;
      const message = e.message || "Save failed";
      const alreadySubmitted = /already/i.test(message);
      setInfoModal({
        title: alreadySubmitted ? "Already submitted" : "Save failed",
        message: alreadySubmitted
          ? "You've already submitted your drawing for this pairing."
          : message,
        actionLabel: "OK",
        onAction: alreadySubmitted
          ? () => navigate("/gallery", { replace: true })
          : null,
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    splitCtx,
    token,
    user,
    submitting,
    pairBlocked,
    baseImageLoadError,
    buildCompositeBlob,
    navigate,
  ]);

  const submitFnRef = useRef(handleSubmitDrawing);
  submitFnRef.current = handleSubmitDrawing;

  useEffect(() => {
    if (!timerEnabled || !splitCtx || pairBlocked) return;
    if (!timerInitialized) return;
    if (secondsLeft !== 0) return;
    if (!ready) return;
    submitFnRef.current();
  }, [secondsLeft, timerEnabled, splitCtx, pairBlocked, ready, timerInitialized]);

  const [historyUi, setHistoryUi] = useState(0);

  const commitState = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    const snap = ctx.getImageData(0, 0, width, height);
    const idx = historyIndexRef.current;
    const hist = historyRef.current;
    let next = [...hist.slice(0, idx + 1), snap];
    while (next.length > MAX_HISTORY) {
      next.shift();
    }
    historyRef.current = next;
    historyIndexRef.current = next.length - 1;
    setHistoryUi((n) => n + 1);
  }, []);

  const canvasCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const applyFill = useCallback(
    (e) => {
      const canvas = canvasRef.current;
      const img = imgRef.current;
      if (!canvas || !img?.complete || !ready) return;
      const w = canvas.width;
      const ch = canvas.height;
      if (w <= 0 || ch <= 0) return;

      const { x, y } = canvasCoords(e);
      const sx = Math.max(0, Math.min(w - 1, Math.floor(x)));
      const sy = Math.max(0, Math.min(ch - 1, Math.floor(y)));

      const tmp = document.createElement("canvas");
      tmp.width = w;
      tmp.height = ch;
      const tx = tmp.getContext("2d");
      if (!tx) return;
      tx.drawImage(img, 0, 0, w, ch);
      tx.drawImage(canvas, 0, 0);
      const composite = tx.getImageData(0, 0, w, ch);

      const { mask, count, target } = floodFillComposite(composite, sx, sy, {
        colorTolerance: FILL_COLOR_TOLERANCE,
      });
      if (!target || count === 0) return;

      const { r: fr, g: fg, b: fb } = hsvToRgb(h, s, v);
      if (
        rgbDistanceSq(fr, fg, fb, target.r, target.g, target.b) <=
        FILL_SKIP_DIST_SQ
      ) {
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const overlayData = ctx.getImageData(0, 0, w, ch);
      const od = overlayData.data;
      for (let i = 0; i < mask.length; i++) {
        if (!mask[i]) continue;
        const p = i * 4;
        od[p] = fr;
        od[p + 1] = fg;
        od[p + 2] = fb;
        od[p + 3] = 255;
      }
      ctx.putImageData(overlayData, 0, 0);
      commitState();
    },
    [ready, canvasCoords, h, s, v, commitState]
  );

  const sampleEyedropper = useCallback(
    (e) => {
      const canvas = canvasRef.current;
      const img = imgRef.current;
      if (!canvas || !ready) return;
      const w = canvas.width;
      const ch = canvas.height;
      if (w <= 0 || ch <= 0) return;

      const { x, y } = canvasCoords(e);
      const sx = Math.max(0, Math.min(w - 1, Math.floor(x)));
      const sy = Math.max(0, Math.min(ch - 1, Math.floor(y)));

      let r;
      let g;
      let b;
      if (img?.complete && img.naturalWidth) {
        const tmp = document.createElement("canvas");
        tmp.width = w;
        tmp.height = ch;
        const tx = tmp.getContext("2d");
        if (!tx) return;
        tx.drawImage(img, 0, 0, w, ch);
        tx.drawImage(canvas, 0, 0);
        const pixel = tx.getImageData(sx, sy, 1, 1).data;
        r = pixel[0];
        g = pixel[1];
        b = pixel[2];
      } else {
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        const pixel = ctx.getImageData(sx, sy, 1, 1).data;
        r = pixel[0];
        g = pixel[1];
        b = pixel[2];
      }
      const picked = rgbToHsv(r, g, b);
      setHsvState({ h: picked.h, s: picked.s, v: picked.v });
    },
    [ready, canvasCoords]
  );

  const restoreHistoryIndex = useCallback((i) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const snap = historyRef.current[i];
    if (!snap) return;
    ctx.putImageData(snap, 0, 0);
  }, []);

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    restoreHistoryIndex(historyIndexRef.current);
    setHistoryUi((n) => n + 1);
  }, [restoreHistoryIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current += 1;
    restoreHistoryIndex(historyIndexRef.current);
    setHistoryUi((n) => n + 1);
  }, [restoreHistoryIndex]);

  const layoutCanvasSquare = useCallback(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!wrap || !canvas || !img?.complete || !img.naturalWidth) return;
    const style = getComputedStyle(wrap);
    const pl = parseFloat(style.paddingLeft) || 0;
    const pr = parseFloat(style.paddingRight) || 0;
    const pt = parseFloat(style.paddingTop) || 0;
    const pb = parseFloat(style.paddingBottom) || 0;
    const contentW = wrap.clientWidth - pl - pr;
    const contentH = wrap.clientHeight - pt - pb;
    let raw = Math.min(contentW, contentH);
    if (raw <= 0) {
      if (contentW > 0) raw = contentW;
      else if (contentH > 0) raw = contentH;
      else return;
    }
    const size = Math.round(
      Math.min(
        TARGET_CANVAS,
        Math.max(MIN_CANVAS, raw > 0 ? raw : TARGET_CANVAS)
      )
    );
    if (canvas.width === size && canvas.height === size) return;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, size, size);
    historyRef.current = [ctx.getImageData(0, 0, size, size)];
    historyIndexRef.current = 0;
    setHistoryUi((n) => n + 1);
    setBaseSize({ cw: size, ch: size });
  }, []);

  const handleBaseImgLoad = useCallback(
    (e) => {
      const img = e.currentTarget;
      imgRef.current = img;
      setBaseImageLoadError(false);
      const wrap = wrapRef.current;
      if (!wrap || !img.naturalWidth) return;
      const run = () => {
        layoutCanvasSquare();
        setReady(true);
      };
      if (wrap.clientWidth === 0) {
        requestAnimationFrame(run);
        return;
      }
      run();
    },
    [layoutCanvasSquare]
  );

  const handleBaseImgError = useCallback(() => {
    if (baseImageRetryCount < 2) {
      setBaseImageRetryCount((n) => n + 1);
      return;
    }
    setBaseImageLoadError(true);
    setReady(false);
  }, [baseImageRetryCount]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      layoutCanvasSquare();
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [layoutCanvasSquare]);

  const handlePointerDown = useCallback(
    (e) => {
      if (!ready) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      e.preventDefault();
      const { x, y } = canvasCoords(e);
      setCursorUi({ visible: true, x, y });

      if (tool === "fill") {
        applyFill(e);
        return;
      }

      if (tool === "eyedropper") {
        sampleEyedropper(e);
        return;
      }

      canvas.setPointerCapture(e.pointerId);

      drawingRef.current = true;
      lastPointRef.current = { x, y };

      const w = strokeWidthForTool(tool, brushThickness);
      const ctx = canvas.getContext("2d");
      ctx.save();
      if (tool === "eraser") {
        ctx.globalCompositeOperation = "destination-out";
        ctx.strokeStyle = "rgba(0,0,0,1)";
        ctx.fillStyle = "rgba(0,0,0,1)";
      } else {
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
      }
      ctx.lineWidth = w;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.restore();
    },
    [ready, tool, color, canvasCoords, brushThickness, applyFill, sampleEyedropper]
  );

  const handlePointerMove = useCallback(
    (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const { x, y } = canvasCoords(e);
      setCursorUi((prev) => ({
        visible: true,
        x,
        y,
      }));
      if (tool === "fill" || tool === "eyedropper") return;
      if (!drawingRef.current || !ready) return;
      e.preventDefault();
      const w = strokeWidthForTool(tool, brushThickness);
      const ctx = canvas.getContext("2d");
      const last = lastPointRef.current;
      ctx.save();
      if (tool === "eraser") {
        ctx.globalCompositeOperation = "destination-out";
        ctx.strokeStyle = "rgba(0,0,0,1)";
      } else {
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = color;
      }
      ctx.lineWidth = w;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.restore();
      lastPointRef.current = { x, y };
    },
    [ready, tool, color, canvasCoords, brushThickness]
  );

  const endStroke = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.globalCompositeOperation = "source-over";
    commitState();
  }, [commitState]);

  const handlePointerUp = useCallback(
    (e) => {
      endStroke();
      try {
        canvasRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    [endStroke]
  );

  const handlePointerLeave = useCallback(() => {
    endStroke();
    setCursorUi((prev) => ({ ...prev, visible: false }));
  }, [endStroke]);

  const handlePointerEnter = useCallback(
    (e) => {
      const { x, y } = canvasCoords(e);
      setCursorUi({ visible: true, x, y });
    },
    [canvasCoords]
  );

  const undoDisabled = useMemo(
    () => historyIndexRef.current <= 0,
    [historyUi]
  );
  const redoDisabled = useMemo(
    () => historyIndexRef.current >= historyRef.current.length - 1,
    [historyUi]
  );

  const showBrushThicknessSlider =
    tool === "brush" || tool === "eraser";
  const thicknessValue = brushThickness;
  const thicknessMax = BRUSH_THICKNESS_MAX;

  const onThicknessChange = (n) => {
    setBrushThickness(
      Math.min(BRUSH_THICKNESS_MAX, Math.max(1, Math.round(n)))
    );
  };

  const baseImageSrc =
    baseImageRetryCount > 0
      ? `${baseImageUrl}${baseImageUrl.includes("?") ? "&" : "?"}r=${baseImageRetryCount}`
      : baseImageUrl;

  if (ctxError === "missing_context") {
    return (
      <div className="draw-page draw-page--center-msg">
        <p className="draw-page__msg">Start from pairing to draw.</p>
        <button
          type="button"
          className="draw-page__msg-btn"
          onClick={() => navigate("/get-pairing")}
        >
          Go to pairing
        </button>
      </div>
    );
  }

  if (!splitCtx) {
    return (
      <div className="draw-page draw-page--center-msg">
        <p className="draw-page__msg">Loading…</p>
      </div>
    );
  }

  if (ctxError === "no_image_set" || ctxError === "no_side_image") {
    return (
      <div className="draw-page draw-page--center-msg">
        <p className="draw-page__msg">
          No split image is configured for this slot.
        </p>
        <button
          type="button"
          className="draw-page__msg-btn"
          onClick={() => navigate("/get-pairing")}
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="draw-page">
      <div className="draw-page__timer" aria-live="polite">
        <span className="draw-page__timer-label">Timer</span>
        <span className="draw-page__timer-value">
          {timerEnabled ? formatTime(secondsLeft) : "—"}
        </span>
      </div>

      <div className="draw-page__frame">
        <aside className="draw-page__toolbar" aria-label="Drawing tools">
            <div className="draw-page__toolbar-top">
              <button
                type="button"
                className="draw-page__pill draw-page__pill--undo"
                onClick={handleUndo}
                disabled={undoDisabled || submitting}
              >
                undo
              </button>
              <button
                type="button"
                className="draw-page__pill draw-page__pill--redo"
                onClick={handleRedo}
                disabled={redoDisabled || submitting}
              >
                redo
              </button>
              <button
                type="button"
                className="draw-page__pill draw-page__pill--save"
                onClick={handleSubmitDrawing}
                disabled={!ready || submitting || pairBlocked}
              >
                {submitting ? "Saving…" : "Save"}
              </button>
            </div>

            <div className="draw-page__wheel-area">
              <div className="draw-page__wheel-row">
                <div className="draw-page__wheel-stack">
                  <ColorWheel
                    h={h}
                    s={s}
                    v={v}
                    onChange={setHsv}
                    size={176}
                  />
                  <span
                    className="draw-page__color-swatch"
                    style={{ backgroundColor: color }}
                    title="Shows your current color"
                    aria-label="Shows your current color"
                  />
                </div>
                <div
                  className="draw-page__tool-icons"
                  role="toolbar"
                  aria-label="Tools"
                >
                  <button
                    type="button"
                    className={`draw-page__tool-icon${
                      tool === "eraser" ? " draw-page__tool-icon--active" : ""
                    }`}
                    aria-pressed={tool === "eraser"}
                    onClick={() => setTool("eraser")}
                    title="Eraser"
                    aria-label="Eraser"
                  >
                    <img src={eraserIcon} alt="" />
                  </button>
                  <button
                    type="button"
                    className={`draw-page__tool-icon${
                      tool === "eyedropper" ? " draw-page__tool-icon--active" : ""
                    }`}
                    aria-pressed={tool === "eyedropper"}
                    onClick={() => setTool("eyedropper")}
                    title="Eyedropper"
                    aria-label="Eyedropper"
                  >
                    <img src={dropperIcon} alt="" />
                  </button>
                  <button
                    type="button"
                    className={`draw-page__tool-icon${
                      tool === "brush" ? " draw-page__tool-icon--active" : ""
                    }`}
                    aria-pressed={tool === "brush"}
                    onClick={() => setTool("brush")}
                    title="Brush"
                    aria-label="Brush"
                  >
                    <img src={brushIcon} alt="" />
                  </button>
                  <button
                    type="button"
                    className={`draw-page__tool-icon${
                      tool === "fill" ? " draw-page__tool-icon--active" : ""
                    }`}
                    aria-pressed={tool === "fill"}
                    onClick={() => setTool("fill")}
                    aria-label="Fill"
                    title="Fill"
                  >
                    <img src={fillIcon} alt="" />
                  </button>
                </div>
              </div>
            </div>

            {showBrushThicknessSlider && (
              <div className="draw-page__thickness">
                <span className="draw-page__thickness-label">
                  {thicknessValue}px
                </span>
                <input
                  type="range"
                  className="draw-page__range draw-page__range--compact"
                  min={1}
                  max={thicknessMax}
                  value={thicknessValue}
                  onChange={(e) =>
                    onThicknessChange(Number(e.target.value))
                  }
                  aria-label="Brush and eraser thickness"
                />
              </div>
            )}
        </aside>

        <div className="draw-page__canvas-wrap" ref={wrapRef}>
          {baseImageLoadError && (
            <div className="draw-page__msg" style={{ marginBottom: 8 }}>
              Could not load base image from S3 (CORS/network).{" "}
              <button
                type="button"
                className="draw-page__msg-btn"
                onClick={() => {
                  setBaseImageLoadError(false);
                  setBaseImageRetryCount(0);
                }}
              >
                Retry
              </button>
            </div>
          )}
          <div className="draw-page__canvas-stack">
            {/* crossOrigin + S3 CORS (GET) for this app origin keeps the canvas exportable (toBlob). */}
            <img
              ref={imgRef}
              key={`${baseImageUrl}-${baseImageRetryCount}`}
              src={baseImageSrc}
              crossOrigin="anonymous"
              alt=""
              draggable={false}
              className="draw-page__base-img"
              onLoad={handleBaseImgLoad}
              onError={handleBaseImgError}
              style={
                baseSize
                  ? {
                      width: baseSize.cw,
                      height: baseSize.ch,
                      objectFit: "contain",
                    }
                  : undefined
              }
            />
            <canvas
              ref={canvasRef}
              className={`draw-page__canvas draw-page__canvas--overlay${
                supportsFinePointer && cursorUi.visible
                  ? " draw-page__canvas--hide-native-cursor"
                  : ""
              }`}
              style={
                baseSize
                  ? { width: baseSize.cw, height: baseSize.ch }
                  : { width: 0, height: 0 }
              }
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerEnter={handlePointerEnter}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onPointerLeave={handlePointerLeave}
            />
            {supportsFinePointer && cursorUi.visible && (
              <div
                className="draw-page__cursor-overlay"
                style={{
                  left: `${cursorUi.x}px`,
                  top: `${cursorUi.y}px`,
                }}
                aria-hidden="true"
              >
                {tool === "brush" || tool === "eraser" ? (
                  <div
                    className={`draw-page__cursor-brush${
                      tool === "eraser"
                        ? " draw-page__cursor-brush--eraser"
                        : " draw-page__cursor-brush--brush"
                    }`}
                    style={
                      tool === "brush"
                        ? {
                            width: brushThickness,
                            height: brushThickness,
                            borderColor: color,
                          }
                        : {
                            width: brushThickness,
                            height: brushThickness,
                          }
                    }
                  />
                ) : (
                  <img
                    src={cursorToolIconSrc}
                    alt=""
                    className="draw-page__cursor-tool-img"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {infoModal && (
        <div className="draw-page-info-modal" role="dialog" aria-modal="true">
          <div className="draw-page-info-modal__backdrop" aria-hidden="true" />
          <div className="draw-page-info-modal__panel">
            {infoModal.title && (
              <h2 className="draw-page-info-modal__title">{infoModal.title}</h2>
            )}
            <p className="draw-page-info-modal__message">{infoModal.message}</p>
            <button
              type="button"
              className="draw-page-info-modal__btn"
              onClick={() => {
                const action = infoModal.onAction;
                setInfoModal(null);
                if (action) action();
              }}
            >
              {infoModal.actionLabel || "OK"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
